import { useEffect, useState } from 'react';
import { useNubUserStore } from '../stores/nub-user-store';
import { useMessage } from '@/shared/hooks/useMessage';
import {
    useGetAllUsers,
    useCreateUser,
    useUpdateUser,
    useGetAllGroups,
    useGetAllAccessLevels,
    useSearchUserByField,
    useSearchByPrimaryGroupAndFullName,
    useGetUsersByInstitution,
} from "@/shared/api/client/nub-user-resource";
import { NubUserDTO } from '../types';
import { PasswordConfig } from '../components/PasswordConfigDialog';
import { UserFilters } from '../components/NubUserFilters';

const defaultPasswordConfig: PasswordConfig = {
    initialPassword: false,
    minimumLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true
};

export const useNubUser = () => {
    const store = useNubUserStore();
    const message = useMessage();
    const [passwordConfig, setPasswordConfig] = useState<PasswordConfig>(defaultPasswordConfig);
    const [currentFilters, setCurrentFilters] = useState<UserFilters>({});

    // Determine which query to use based on filters
    const shouldUseSearch = Object.keys(currentFilters).length > 0;
    
    // Main users query (used when no filters)
    const {
        data: usersResponse,
        isLoading: isLoadingUsers,
        error: usersError,
        refetch: refetchUsers
    } = useGetAllUsers({
        query: { enabled: !shouldUseSearch }
    });

    // Search by field query
    const {
        data: searchResponse,
        isLoading: isLoadingSearch,
        error: searchError,
        refetch: refetchSearch
    } = useSearchUserByField(
        currentFilters.searchField || '',
        currentFilters.searchValue || '',
        {
            query: { 
                enabled: shouldUseSearch && !!(currentFilters.searchField && currentFilters.searchValue)
            }
        }
    );

    // Search by primary group and name query
    const {
        data: groupSearchResponse,
        isLoading: isLoadingGroupSearch,
        error: groupSearchError,
        refetch: refetchGroupSearch
    } = useSearchByPrimaryGroupAndFullName(
        currentFilters.primaryGroup || '',
        currentFilters.searchValue || '',
        {
            query: { 
                enabled: shouldUseSearch && !!(currentFilters.primaryGroup && currentFilters.searchValue && !currentFilters.searchField)
            }
        }
    );

    // Search by institution query
    const {
        data: institutionResponse,
        isLoading: isLoadingInstitution,
        error: institutionError,
        refetch: refetchInstitution
    } = useGetUsersByInstitution(
        currentFilters.institution || '',
        {
            query: { 
                enabled: shouldUseSearch && !!currentFilters.institution
            }
        }
    );

    // Queries
    const {
        data: groupsResponse,
        isLoading: isLoadingGroups
    } = useGetAllGroups({
        query: { enabled: true }
    });

    const {
        data: accessLevelsResponse,
        isLoading: isLoadingAccessLevels
    } = useGetAllAccessLevels({
        query: { enabled: true }
    });

    // Mutations
    const createMutation = useCreateUser();
    const updateMutation = useUpdateUser();

    // Update store when data changes
    useEffect(() => {
        let userData = null;
        
        if (!shouldUseSearch && usersResponse) {
            userData = usersResponse;
        } else if (searchResponse) {
            userData = searchResponse;
        } else if (groupSearchResponse) {
            userData = groupSearchResponse;
        } else if (institutionResponse) {
            userData = institutionResponse;
        }

        if (userData) {
            // Apply client-side filtering for enabled status if specified
            let filteredData = userData;
            if (currentFilters.enabled !== undefined) {
                filteredData = userData.filter(user => user.enabled === currentFilters.enabled);
            }
            store.setUsers(filteredData);
        }
    }, [usersResponse, searchResponse, groupSearchResponse, institutionResponse, currentFilters, shouldUseSearch]);

    useEffect(() => {
        if (groupsResponse) {
            store.setGroups(groupsResponse);
        }
    }, [groupsResponse]);

    useEffect(() => {
        if (accessLevelsResponse) {
            store.setAccessLevels(accessLevelsResponse);
        }
    }, [accessLevelsResponse]);

    // Load password config from backend or local storage
    useEffect(() => {
        const loadPasswordConfig = async () => {
            try {
                // You can replace this with an API call to load config from backend
                const savedConfig = localStorage.getItem('passwordConfig');
                if (savedConfig) {
                    setPasswordConfig(JSON.parse(savedConfig));
                }
            } catch (error) {
                console.error('Failed to load password config:', error);
            }
        };

        loadPasswordConfig();
    }, []);

    // Common handling for modal actions
    const handleUserAction = (user: NubUserDTO | null, mode: 'view' | 'add' | 'edit' | 'delete') => {
        store.setActiveUser(user);
        store.setActionMode(mode);
        store.setModalOpen(true);
    };

    // Password validation based on config
    const validatePassword = (password: string): boolean => {
        if (password.length < passwordConfig.minimumLength) return false;
        if (passwordConfig.requireUppercase && !/[A-Z]/.test(password)) return false;
        if (passwordConfig.requireLowercase && !/[a-z]/.test(password)) return false;
        if (passwordConfig.requireNumbers && !/\d/.test(password)) return false;
        if (passwordConfig.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) return false;
        return true;
    };

    // CRUD operations with password handling
    const createUser = async (data: NubUserDTO) => {
        if (data.password && !validatePassword(data.password)) {
            message.error('Password does not meet requirements');
            throw new Error('Invalid password');
        }

        try {
            const response = await createMutation.mutateAsync({
                data: {
                    ...data,
                    // Only include password if admin sets it
                    password: passwordConfig.initialPassword ? data.password : undefined
                }
            });
            await refetchUsers();
            return response.data;
        } catch (error: any) {
            // Extract the actual error message from the backend response
            const errorMessage = error?.response?.data?.message ||
                                error?.response?.data?.error ||
                                error?.message ||
                                'Failed to create user';
            message.error(errorMessage);
            throw error;
        }
    };

    const updateUser = async (userId: string, data: NubUserDTO) => {
        if (data.password && !validatePassword(data.password)) {
            message.error('Password does not meet requirements');
            throw new Error('Invalid password');
        }

        try {
            const response = await updateMutation.mutateAsync({
                userId,
                data: {
                    ...data,
                    // Include password only if resetting or if admin sets it
                    password: data.resetPassword && passwordConfig.initialPassword ? data.password : undefined
                }
            });
            await refetchUsers();
            return response.data;
        } catch (error: any) {
            // Extract the actual error message from the backend response
            const errorMessage = error?.response?.data?.message || 
                                error?.response?.data?.error || 
                                error?.message || 
                                'Failed to update user';
            message.error(errorMessage);
            throw error;
        }
    };

    const updatePasswordConfig = async (config: PasswordConfig) => {
        try {
            // You can add an API call here to save config to backend
            setPasswordConfig(config);
            localStorage.setItem('passwordConfig', JSON.stringify(config));
        } catch (error) {
            message.error('Failed to update password configuration');
            throw error;
        }
    };

    // Filter handling
    const handleFiltersChange = (filters: UserFilters) => {
        setCurrentFilters(filters);
    };

    const clearFilters = () => {
        setCurrentFilters({});
    };

    // Determine current loading state
    const isLoading = isLoadingUsers || isLoadingGroups || isLoadingAccessLevels || 
                     isLoadingSearch || isLoadingGroupSearch || isLoadingInstitution;

    // Determine current error state
    const error = usersError || searchError || groupSearchError || institutionError;

    // Enhanced refetch that handles current filter state
    const refetchData = async () => {
        if (!shouldUseSearch) {
            await refetchUsers();
        } else if (currentFilters.searchField && currentFilters.searchValue) {
            await refetchSearch();
        } else if (currentFilters.primaryGroup && currentFilters.searchValue) {
            await refetchGroupSearch();
        } else if (currentFilters.institution) {
            await refetchInstitution();
        }
    };

    return {
        // State
        users: store.users,
        activeUser: store.activeUser,
        groups: store.groups,
        accessLevels: store.accessLevels,
        actionMode: store.actionMode,
        isModalOpen: store.isModalOpen,
        isLoading,
        error,
        passwordConfig,
        currentFilters,

        // Actions
        setActiveUser: store.setActiveUser,
        setActionMode: store.setActionMode,
        setModalOpen: store.setModalOpen,
        handleUserAction,
        handleFiltersChange,
        clearFilters,

        // CRUD operations
        createUser,
        updateUser,
        refetchUsers: refetchData,
        updatePasswordConfig,
        validatePassword,
    };
};